import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { roles } from "constants/";
import userApi from "api/user";
import { DatePicker } from "components/";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { getUser } from "utils/";
import { rolesShowPosition } from "features/users/roles";

export default function ModalCreated({
  setOpenModal,
  openModal,
  isCreated = false,
  optionsStore,
  optionsRole,
  dataUser,
  openNotificationWithIcon,
  onSearch,
}) {
  const [form] = Form.useForm();
  const role = Form.useWatch("role", form);
  const watchShops = Form.useWatch("shops", form);
  const shops = useMemo(() => watchShops || [], [watchShops]);

  const { t } = useTranslation()

  const onCancel = () => {
    form.resetFields();
    setOpenModal(false);
  };

  useEffect(() => {
    if (!isCreated && openModal) {
      form.setFieldValue("id", dataUser._id);
      form.setFieldValue("user_name", dataUser.user_name);
      form.setFieldValue("employee_code", dataUser.employee_code);
      form.setFieldValue("name", dataUser.name);
      form.setFieldValue("numAbsence", dataUser.numAbsence);
      form.setFieldValue("lastYearNumAbsence", dataUser.lastYearNumAbsence);
      form.setFieldValue("role", dataUser.role.toString());
      form.setFieldValue("workedAt", moment(dataUser?.shops[0]?.workedAt));

      if ([roles.shopManager, roles.staff, roles.staffShop].includes(+dataUser.role)) {
        form.setFieldValue("shop", dataUser?.shops[0]?.shop?._id);
      }

      if ([roles.areaManager, roles.departmentManager, roles.springer].includes(+dataUser.role)) {
        form.setFieldValue(
          "shops",
          dataUser.shops.map((item) => ({
            shop: item.shop._id,
          }))
        );
      }
    }
  }, [dataUser, form, isCreated, openModal]);

  const onSubmit = async (values) => {
    if (
      (([roles.areaManager, roles.departmentManager, roles.springer].includes(+values.role)) && !Array.isArray(values?.shops)) ||
      values?.shops?.length == 0
    ) {
      return openNotificationWithIcon("error", t('error'), t('shopRequired'));
    }

    values.role = Number(values.role);

    try {
      if (isCreated) {
        if ([roles.shopManager, roles.staff, roles.staffShop].includes(+values.role)) {
          values.shops = [
            {
              shop: values.shop,
              workedAt: values.workedAt,
            },
          ];

          values.createdAt = values.workedAt;

          delete values.shop;
          delete values.workedAt;
        }
        const res = await userApi.createUser(values);
        if (res?.success) {
          openNotificationWithIcon("success", t('notification'), t('createEmployeeSuccess'));
          onCancel();
          onSearch();
        }
      } else {
        switch (values.role) {
          case roles.shopManager:
            values.shops = [
              {
                shop: values.shop,
                workedAt: values.workedAt,
              },
            ];

            delete values.workedAt;
            delete values.shop;

            break;
          case roles.staff:
          case roles.staffShop:
            delete values.workedAt;
            delete values.shop;
            delete values.user_name

            break;
          case roles.admin:
            values.shops = [];
            break;
          default:
            break;
        }

        const res = await userApi.editUser(dataUser._id, values);
        if (res?.success) {
          openNotificationWithIcon("success", t('notification'), t('updateEmployeeSuccess'));
          onCancel();
          onSearch();
        }
      }
    } catch (error) {
      console.log("🚀 ~ error:", error)
      openNotificationWithIcon("error", t('error'), error?.response?.data?.message || t('hasError'));
    }
  };

  const [stores, setStore] = useState([]);

  const listRole = useMemo(
    () =>
      cloneDeep(optionsRole).map((role) => {
        if (role.value == roles.admin) {
          return {
            ...role,
            disabled: true,
          };
        } else {
          return {
            ...role,
            disabled: false,
          };
        }
      }),
    [optionsRole]
  );

  useEffect(() => {
    const newOption = optionsStore.map((option) => {
      if (shops.some((el) => el?.shop === option.value)) {
        return {
          ...option,
          disabled: true,
        };
      } else {
        return {
          ...option,
          disabled: false,
        };
      }
    });
    setStore(newOption);
  }, [shops, optionsStore]);

  return (
    <Modal
      title={isCreated ? t('addEmployee') : t('updateEmployee')}
      centered
      open={openModal}
      footer={null}
      onCancel={onCancel}
      width={768}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        {
          (rolesShowPosition.includes(+getUser().role)) &&
          <Form.Item
            name="role"
            label={t('position')}
            required
            disabled={!isCreated}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select options={listRole} allowClear />
          </Form.Item>
        }
        {
          role != roles.departmentManager && <Form.Item
            name="employee_code"
            label={t('employeeCode')}
            required
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        }
        <Form.Item
          name="name"
          label={t('fullName')}
          required
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input maxLength={32} />
        </Form.Item>
        {
          (isCreated && getUser().role == roles.springer) || (getUser().role != roles.springer && ![roles.staff, roles.staffShop].includes(+role)) && <Form.Item
            name="user_name"
            label={t('userName')}
            maxLength={64}
            required
            rules={[
              {
                required: true,
              },
              {
                min: 4,
                message: t('minLength', { value: 4 }),
              },
            ]}
          >
            <Input />
          </Form.Item>
        }
        {(isCreated && ![roles.staff, roles.staffShop].includes(+role)) && (
          <>
            <Form.Item
              name="password"
              label={t('password')}
              maxLength={32}
              required
              rules={[
                {
                  required: true,
                },
                {
                  min: 4,
                  message: t('minLength', { value: 4 }),
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirm"
              label={t('confirmPassword')}
              maxLength={32}
              required
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('notMatchPassword')));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </>
        )}
        {([roles.staff, roles.staffShop, roles.springer].includes(+role)) && (
          <Form.Item
            name="numAbsence"
            label={t('numOfAbsence')}
            required
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber min={0} max={30} style={{ width: "100%" }} />
          </Form.Item>
        )}
        {((isCreated && [roles.staff, roles.staffShop].includes(+role)) || role == roles.shopManager) && (
          <>
            <Form.Item
              name="shop"
              label={t('shop')}
              required
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select options={optionsStore} allowClear />
            </Form.Item>
          </>
        )}
        {
          ([roles.departmentManager, roles.areaManager, roles.springer].includes(+role)) &&
          <Form.List name="shops">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8, width: "100%" }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "shop"]}
                      label={t('shop')}
                      required
                      style={{ width: "700px" }}
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Select options={stores} allowClear />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                    {t('addShop')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        }
        {(isCreated && [roles.staff, roles.staffShop].includes(+role)) && (
          <Form.Item
            name="workedAt"
            label={t('workStartDate')}
            required
            rules={[{ required: true }]}
          >
            <DatePicker placeholder='' style={{ width: "200px" }} format="YYYY-MM-DD" />
          </Form.Item>
        )}
        {
          ([roles.staff, roles.staffShop].includes(+role)) &&
          <Form.Item
            name="lastYearNumAbsence"
            label={t('remaingLastYearAbsence')}
          >
            <InputNumber style={{ width: "200px" }} />
          </Form.Item>
        }
        <Row gutter={16}>
          <Col span={4} offset={8}>
            <Form.Item>
              <Button style={{ width: "100%" }} htmlType="submit">
                Ok
              </Button>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Button style={{ width: "100%" }} onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
